import React, { FC, FormEventHandler, MouseEventHandler, useState } from 'react'
import useAxios from '../../hooks/useAxios'
import { useDispatch } from 'react-redux'
import { logError, updateUserProfile } from '../../store/slices/app'
import { AuthenticatedUser } from '../../types'
import { isAxiosError } from 'axios'

interface Props {
  label: string
  initial: string
  placeholder?: string
  attribute: string
  clearOnSave?: boolean
}

const UserProfileSettingRow: FC<Props> = ({ label, initial, placeholder, attribute, clearOnSave = false }) => {
  const instance = useAxios()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState('')
  const [value, setValue] = useState(initial)

  const handleCancel: MouseEventHandler = (e) => {
    e.preventDefault()
    if (isLoading) return
    setValue(initial)
    setError('')
    setIsEditing(false)
  }

  interface UpdateProfileResponse {
    profile: AuthenticatedUser
  }

  const handleSave: FormEventHandler = async (e) => {
    e.preventDefault()
    if (isLoading) return
    setIsLoading(true)
    setError('')

    try {
      const response = await instance.put<UpdateProfileResponse>('ac/v1/users/me', {
        [attribute]: value,
      })

      dispatch(updateUserProfile(response.data.profile))
      setIsEditing(false)
      if (clearOnSave) {
        setValue('')
      }
    } catch (e) {
      dispatch(logError(e))
      let message = 'Sorry an unknown error occurred.'

      if (isAxiosError(e) && e.response?.data?.data?.params[attribute]) {
        message = e.response?.data?.data?.params[attribute]
      }

      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const LabelComponent = <span className="d-inline-block w-100 placeholder-text">{label}</span>

  if (isEditing) {
    return (
      <form className="px-4 py-3" onSubmit={handleSave}>
        {LabelComponent}
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            disabled={isLoading}
            placeholder={label}
            value={value}
            autoFocus
            onChange={(e) => setValue(e.target.value)}
          />
          <div className="input-group-append">
            <button className="btn btn-secondary" disabled={isLoading} type="submit">
              {isLoading ? 'Saving' : 'Save'}
            </button>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          {error && <span className="d-block invalid-feedback flex-grow-1 mr-1">{error}</span>}
          <button
            type="button"
            disabled={isLoading}
            className="text-secondary btn btn-link px-3 ml-auto"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    )
  }

  return (
    <div className="px-4 py-3">
      <div className="d-flex flex-wrap align-items-center">
        <div className="flex-grow-1">
          {LabelComponent}
          <span className="d-inline-block w-100">{placeholder || initial}</span>
        </div>
        <div>
          <button
            type="button"
            className="text-secondary bg-light-blue p-1 btn btn-link"
            onClick={() => setIsEditing(true)}
          >
            <span className="icon-pencil"></span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserProfileSettingRow
